import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { TpETLComponentTypeManifest } from '../interfaces';
import { TpETLObjectTypes } from '../../etl-data-types/interfaces';
import { TpETLPipelineComponent, TpETLDryRun, TpETLDryRunComponentResult, TpETLDataContext } from '../../interfaces';
import { addDryRunObjectTable } from '../../utils';
import { ETLDryRunObjectTableAddColumn } from '../utils';
import ParamSet from '../../edit-etl-component/ParamSet';

import styles from '../../edit-etl-component/editETLComponentForm.module.scss';
import { addETLDryRunError } from '../../etlDryRun';

const PARAM_TABLE_ID = 'selectedTableId';

function performDryRun(
  dryRun: TpETLDryRun,
  comp: TpETLPipelineComponent,
  assetEtlDataContext?: TpETLDataContext | null,
): TpETLDryRunComponentResult {
  const outputTableObj = addDryRunObjectTable(dryRun, comp.outputObjectIds['result'], comp);
  try {
    if (!assetEtlDataContext) throw `Asset data context is not available`;

    const selectedTableId = comp.params[PARAM_TABLE_ID];

    if (!selectedTableId) throw `No data table selected`;

    const selectedTable = assetEtlDataContext.dataTables.find((table) => table.dataObjectId === selectedTableId);

    if (!selectedTable) throw `Selected table not found in the data context`;

    selectedTable.columns.forEach((col) => {
      ETLDryRunObjectTableAddColumn(outputTableObj, col.identifier, col.name, col.description, col.dataType);
    });
  } catch (error) {
    addETLDryRunError(dryRun, String(error), comp.id, PARAM_TABLE_ID);
  }

  return {
    abort: false,
  };
}

function renderCustomConfigParams(
  dryRun: TpETLDryRun,
  comp: TpETLPipelineComponent,
  updateEditingComponent: (newComp: TpETLPipelineComponent) => void,
  ontology?: any,
  etlDataContext?: TpETLDataContext | null,
): any {
  return (
    <div key={`select_data_table`} className={styles.section}>
      <h3>Select Data Table</h3>
      {!etlDataContext && (
        <div className={styles.errors}>
          <div>Asset data context is not available</div>
        </div>
      )}
      {etlDataContext && etlDataContext.dataTables.length === 0 && (
        <div className={styles.errors}>
          <div>No data tables available in the current data context</div>
        </div>
      )}
      {etlDataContext && etlDataContext.dataTables.length > 0 && (
        <>
          <div>
            Choose as which existing data table available from <b>{etlDataContext.assetName}</b> should be imported into
            the pipeline.
          </div>
          <ParamSet
            params={[
              {
                name: 'Data Table',
                component: (
                  <select
                    value={comp.params[PARAM_TABLE_ID]}
                    onChange={(ev) => {
                      comp.params[PARAM_TABLE_ID] = ev.target.value;
                      updateEditingComponent(comp);
                    }}
                  >
                    <option key="_empty_" value={''}>
                      ---- Select Data Table ----
                    </option>
                    {etlDataContext.dataTables.map((dataTable) => (
                      <option key={dataTable.dataObjectId} value={dataTable.dataObjectId}>
                        {dataTable.name}
                      </option>
                    ))}
                  </select>
                ),
              },
            ]}
          />
        </>
      )}
    </div>
  );
}

function getDataAvailablility(assetEtlDataContext: TpETLDataContext | null): { available: boolean; message: string } {
  if (!assetEtlDataContext) return { available: false, message: 'Asset data context is not available' };
  if (assetEtlDataContext.dataTables.length === 0)
    return { available: false, message: 'No data tables available in the current data context' };
  return { available: true, message: '' };
}

export const manifestETLSelectDataTable: TpETLComponentTypeManifest = {
  id: 'SelectDataTable',
  name: 'Select Data Table',
  description: 'Select existing data table from the current data context.',
  categoryId: 'source',
  icon: <FontAwesomeIcon icon={['fas', 'table']} />,
  performDryRun,
  renderCustomConfigParams,
  getDataAvailablility,
  inputs: [],
  outputs: [
    {
      id: 'result',
      name: 'Selected table',
      type: TpETLObjectTypes.TABLE,
      description: 'Selected table from the current data context.',
      columns: [],
    },
  ],
  params: [],
};
